
import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import SEOHead from '../../../components/SEOHead';

// ─── 카운트업 훅 ───────────────────────────────────────────────
function useCountUp(target: string, duration = 1400, trigger: boolean = false) {
  const [display, setDisplay] = useState('0');

  useEffect(() => {
    if (!trigger) return;
    const numMatch = target.match(/[\d,.]+/);
    if (!numMatch) {
      setDisplay(target);
      return;
    }

    const rawNum = parseFloat(numMatch[0].replace(/,/g, ''));
    const prefix = target.slice(0, target.indexOf(numMatch[0]));
    const suffix = target.slice(target.indexOf(numMatch[0]) + numMatch[0].length);
    const hasDecimal = numMatch[0].includes('.');
    const decimalPlaces = hasDecimal ? numMatch[0].split('.')[1].length : 0;

    let start: number | null = null;
    const step = (timestamp: number) => {
      if (!start) start = timestamp;
      const progress = Math.min((timestamp - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = rawNum * eased;
      const formatted = hasDecimal
        ? current.toFixed(decimalPlaces)
        : Math.floor(current).toLocaleString();
      setDisplay(`${prefix}${formatted}${suffix}`);
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [trigger, target, duration]);

  return display;
}

// ─── 스탯 카드 ────────────────────────────────────────────────
function StatCard({
  icon,
  value,
  label,
  sub,
  delay,
}: {
  icon: string;
  value: string;
  label: string;
  sub: string;
  delay: number;
}) {
  const [hovered, setHovered] = useState(false);
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const displayValue = useCountUp(value, 1400, visible);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="relative flex flex-col items-center text-center gap-1.5 py-4 px-3 rounded-2xl cursor-default transition-all duration-300 overflow-hidden"
      style={{
        background: hovered ? 'rgba(249,115,22,0.12)' : 'transparent',
        transform: hovered ? 'translateY(-4px)' : 'translateY(0)',
        transitionDelay: `${delay}ms`,
      }}
    >
      {/* 배경 글로우 */}
      <div
        className="absolute inset-0 rounded-2xl transition-opacity duration-300 pointer-events-none"
        style={{
          background:
            'radial-gradient(circle at 50% 50%, rgba(249,115,22,0.18) 0%, transparent 70%)',
          opacity: hovered ? 1 : 0,
        }}
      />
      <div
        className="w-11 h-11 flex items-center justify-center rounded-full transition-all duration-300 relative z-10"
        style={{
          background: hovered
            ? 'rgba(249,115,22,0.35)'
            : 'rgba(249,115,22,0.15)',
          transform: hovered ? 'scale(1.15)' : 'scale(1)',
          boxShadow: hovered
            ? '0 0 18px rgba(249,115,22,0.5)'
            : 'none',
        }}
      >
        <i className={`${icon} text-orange-400 text-xl`}></i>
      </div>
      <p
        className="text-white text-xl md:text-2xl font-extrabold relative z-10 transition-all duration-300"
        style={{
          textShadow: hovered
            ? '0 0 12px rgba(249,115,22,0.7)'
            : 'none',
        }}
      >
        {displayValue}
      </p>
      <p className="text-gray-300 text-xs font-semibold relative z-10">
        {label}
      </p>
      <p className="text-gray-500 text-xs relative z-10">{sub}</p>
      {/* 하단 강조 바 */}
      <div
        className="absolute bottom-0 left-1/2 -translate-x-1/2 h-0.5 bg-orange-500 rounded-full transition-all duration-300"
        style={{ width: hovered ? '60%' : '0%' }}
      />
    </div>
  );
}

// ─── 공정 팝업 데이터 ─────────────────────────────────────────
const processDetails = [
  {
    step: '01',
    icon: 'ri-pencil-ruler-2-line',
    title: 'CAD 설계',
    desc: '도면 검토 및 자체 설계',
    cardBg: 'bg-gradient-to-br from-orange-50 to-orange-100',
    cardBorder: 'border-orange-200',
    iconBg: 'bg-orange-500',
    stepColor: 'text-orange-500',
    labelColor: 'text-orange-600',
    glowColor: 'rgba(249,115,22,0.15)',
    details: [
      {
        icon: 'ri-file-list-3-line',
        title: '도면 검토',
        body: '고객 제공 도면(DXF, DWG, PDF)을 정밀 검토하여 가공 가능 여부를 사전 확인합니다.',
      },
      {
        icon: 'ri-pencil-ruler-2-line',
        title: '자체 설계',
        body: '도면이 없는 경우 현장 실측 또는 샘플 기반으로 자체 CAD 설계를 진행합니다.',
      },
      {
        icon: 'ri-settings-4-line',
        title: '네스팅 최적화',
        body: '소재 낭비를 최소화하는 네스팅(Nesting) 최적화로 원가 절감을 실현합니다.',
      },
    ],
    color: 'orange',
  },
  {
    step: '02',
    icon: 'ri-focus-3-line',
    title: '레이저 절단',
    desc: '파이버 레이저 정밀 절단',
    cardBg: 'bg-gradient-to-br from-red-50 to-red-100',
    cardBorder: 'border-red-200',
    iconBg: 'bg-red-500',
    stepColor: 'text-red-500',
    labelColor: 'text-red-600',
    glowColor: 'rgba(239,68,68,0.15)',
    details: [
      {
        icon: 'ri-focus-3-line',
        title: '6,000W 파이버 레이저',
        body: 'Bystronic Smart Fiber 6kW 장비로 철판 최대 30mm, 스테인리스·알루미늄 최대 25mm까지 절단합니다.',
      },
      {
        icon: 'ri-crosshair-line',
        title: '±0.05mm 정밀도',
        body: '위치 정밀도 ±0.05mm, 반복 정밀도 ±0.03mm의 고정밀 절단으로 후가공 최소화합니다.',
      },
      {
        icon: 'ri-speed-line',
        title: '최대 140m/min',
        body: '고속 절단으로 납기 단축 및 대량 생산 대응이 가능합니다.',
      },
    ],
    color: 'red',
  },
  {
    step: '03',
    icon: 'ri-tools-line',
    title: '절곡 가공',
    desc: 'CNC 프레스 브레이크 성형',
    cardBg: 'bg-gradient-to-br from-amber-50 to-amber-100',
    cardBorder: 'border-amber-200',
    iconBg: 'bg-amber-500',
    stepColor: 'text-amber-500',
    labelColor: 'text-amber-600',
    glowColor: 'rgba(245,158,11,0.15)',
    details: [
      {
        icon: 'ri-tools-line',
        title: 'CNC 프레스 브레이크',
        body: '최대 굽힘 길이 3,000mm, 최대 하중 130Ton의 CNC 프레스 브레이크로 다양한 형상을 성형합니다.',
      },
      {
        icon: 'ri-angle-line',
        title: '각도 정밀도 ±0.5°',
        body: '백게이지 정밀도 ±0.1mm, 각도 정밀도 ±0.5°로 반복 재현성이 뛰어납니다.',
      },
      {
        icon: 'ri-stack-line',
        title: '다양한 소재 대응',
        body: '철판 12mm, 스테인리스 9mm, 알루미늄 10mm까지 절곡 가공이 가능합니다.',
      },
    ],
    color: 'amber',
  },
  {
    step: '04',
    icon: 'ri-fire-line',
    title: '용접 가공',
    desc: 'TIG / MIG / CO₂ 용접',
    cardBg: 'bg-gradient-to-br from-sky-50 to-sky-100',
    cardBorder: 'border-sky-200',
    iconBg: 'bg-sky-500',
    stepColor: 'text-sky-500',
    labelColor: 'text-sky-600',
    glowColor: 'rgba(14,165,233,0.15)',
    details: [
      {
        icon: 'ri-fire-line',
        title: 'TIG 정밀 용접',
        body: '전류 범위 5~350A, 스테인리스·알루미늄 등 정밀 용접에 최적화된 TIG 용접을 제공합니다.',
      },
      {
        icon: 'ri-flashlight-line',
        title: 'MIG / CO₂ 용접',
        body: 'MIG 30~500A, CO₂ 60~500A 범위로 철판 구조물 및 대형 부재 용접에 활용합니다.',
      },
      {
        icon: 'ri-shield-check-line',
        title: '이종 소재 용접',
        body: '철·스테인리스·알루미늄 이종 소재 간 용접도 사전 협의를 통해 조건부 대응 가능합니다.',
      },
    ],
    color: 'sky',
  },
  {
    step: '05',
    icon: 'ri-settings-3-line',
    title: '조립 · 납품',
    desc: '완제품 조립 및 검수 납품',
    cardBg: 'bg-gradient-to-br from-emerald-50 to-emerald-100',
    cardBorder: 'border-emerald-200',
    iconBg: 'bg-emerald-500',
    stepColor: 'text-emerald-500',
    labelColor: 'text-emerald-600',
    glowColor: 'rgba(16,185,129,0.15)',
    details: [
      {
        icon: 'ri-settings-3-line',
        title: '완제품 조립',
        body: '절단·절곡·용접된 부품을 도면 기준으로 정밀 조립하여 완제품 상태로 납품합니다.',
      },
      {
        icon: 'ri-search-eye-line',
        title: '품질 검수',
        body: '치수 검사, 외관 검사, 용접 비드 검사 등 다단계 품질 검수를 거쳐 불량률을 최소화합니다.',
      },
      {
        icon: 'ri-truck-line',
        title: '신속 납품',
        body: '자체 물류 네트워크를 통해 수도권 및 전국 당일·익일 납품 서비스를 제공합니다.',
      },
    ],
    color: 'emerald',
  },
];

const colorPopup: Record<
  string,
  { bg: string; icon: string; badge: string; border: string }
> = {
  orange: {
    bg: 'bg-orange-50',
    icon: 'text-orange-500',
    badge: 'bg-orange-500',
    border: 'border-orange-200',
  },
  red: {
    bg: 'bg-red-50',
    icon: 'text-red-500',
    badge: 'bg-red-500',
    border: 'border-red-200',
  },
  amber: {
    bg: 'bg-amber-50',
    icon: 'text-amber-500',
    badge: 'bg-amber-500',
    border: 'border-amber-200',
  },
  sky: {
    bg: 'bg-sky-50',
    icon: 'text-sky-500',
    badge: 'bg-sky-500',
    border: 'border-sky-200',
  },
  emerald: {
    bg: 'bg-emerald-50',
    icon: 'text-emerald-500',
    badge: 'bg-emerald-500',
    border: 'border-emerald-200',
  },
  gray: {
    bg: 'bg-gray-50',
    icon: 'text-gray-600',
    badge: 'bg-gray-700',
    border: 'border-gray-200',
  },
};

// ─── 프로그레스 바 컴포넌트 ───────────────────────────────────
function ThicknessBar({
  thickness,
  maxRef,
  color,
}: {
  thickness: string;
  maxRef: number;
  color: string;
}) {
  const numMatch = thickness.match(/[\d.]+/g);
  const maxVal = numMatch ? parseFloat(numMatch[numMatch.length - 1]) : 0;
  const percent = Math.min((maxVal / maxRef) * 100, 100);

  const barColor: Record<string, string> = {
    orange: 'bg-orange-500',
    gray: 'bg-gray-600',
    red: 'bg-red-500',
  };

  return (
    <div className="mt-2">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] text-gray-400 font-medium uppercase tracking-wide">
          두께 범위
        </span>
        <span className="text-[10px] font-bold text-gray-600">{thickness}</span>
      </div>
      <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-1000 ease-out ${
            barColor[color] ?? 'bg-orange-500'
          }`}
          style={{ width: `${percent}%` }}
        />
      </div>
      <div className="flex justify-between mt-0.5">
        <span className="text-[9px] text-gray-300">0mm</span>
        <span className="text-[9px] text-gray-300">{maxRef}mm</span>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
export default function CapabilityPage() {
  const navigate = useNavigate();

  // ---- navigation & UI state ------------------------------------
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [equipmentDropdownOpen, setEquipmentDropdownOpen] = useState(false);
  const [productsDropdownOpen, setProductsDropdownOpen] = useState(false);
  const [aboutDropdownOpen, setAboutDropdownOpen] = useState(false);
  const [mobileEquipmentOpen, setMobileEquipmentOpen] = useState(false);
  const [mobileProductsOpen, setMobileProductsOpen] = useState(false);
  const [mobileAboutOpen, setMobileAboutOpen] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'laser' | 'bending' | 'welding'>(
    'laser'
  );
  const [prevTab, setPrevTab] = useState<'laser' | 'bending' | 'welding'>(
    'laser'
  );
  const [tabAnimating, setTabAnimating] = useState(false);
  const [tabVisible, setTabVisible] = useState(true);
  const [popupIndex, setPopupIndex] = useState<number | null>(null);

  // ---- helpers -------------------------------------------------
  const handleTabChange = (tab: 'laser' | 'bending' | 'welding') => {
    if (tab === activeTab || tabAnimating) return;
    setTabAnimating(true);
    setTabVisible(false);
    setTimeout(() => {
      setPrevTab(activeTab);
      setActiveTab(tab);
      setTabVisible(true);
      setTabAnimating(false);
    }, 260);
  };

  const handleMapNav = () => {
    navigate('/');
    setTimeout(
      () =>
        document.getElementById('map')?.scrollIntoView({
          behavior: 'smooth',
        }),
      400
    );
  };
  const handleAboutNav = () => {
    navigate('/');
    setTimeout(
      () =>
        document.getElementById('about')?.scrollIntoView({
          behavior: 'smooth',
        }),
      400
    );
  };
  const handleProcessNav = () => {
    navigate('/');
    setTimeout(
      () =>
        document.getElementById('process')?.scrollIntoView({
          behavior: 'smooth',
        }),
      400
    );
  };

  // ---- navigation data -----------------------------------------
  const navLinks = [
    {
      href: '/#about',
      label: '회사소개',
      type: 'dropdown' as const,
      subMenu: [
        { href: '/#about', label: '회사소개', action: handleAboutNav },
        { href: '/history', label: '연혁' },
        { href: '/organization', label: '조직도' },
        { href: '/#map', label: '오시는 길', action: handleMapNav },
      ],
    },
    {
      href: '/#process',
      label: '생산공정',
      type: 'scroll' as const,
      action: handleProcessNav,
    },
    {
      href: '#equipment',
      label: '보유장비',
      type: 'dropdown' as const,
      subMenu: [
        { href: '/equipment/production', label: '생산장비' },
        { href: '/equipment/welding', label: '용접장비' },
        { href: '/equipment/capability', label: '가공능력' },
        { href: '/equipment/materials', label: '가공 가능자재' },
      ],
    },
    {
      href: '#products',
      label: '제조물품',
      type: 'dropdown' as const,
      subMenu: [{ href: '/gallery', label: '갤러리' }],
    },
  ];

  // ---- equipment specifications ---------------------------------
  const equipmentSpecs = {
    laser: {
      title: '레이저 가공',
      icon: 'ri-focus-3-line',
      color: 'orange',
      maxRef: 30,
      machines: [
        {
          name: 'Bystronic Smart Fiber',
          image:
            'https://public.readdy.ai/ai/img_res/e37e2896723aef15927d904f2c0737f4.jpg',
          specs: [
            { label: '레이저 출력', value: '6,000W' },
            { label: '가공 범위', value: '3,048 × 1,524 mm' },
            { label: '최대 두께 (철판)', value: '30 mm' },
            { label: '최대 두께 (스테인리스)', value: '25 mm' },
            { label: '최대 두께 (알루미늄)', value: '25 mm' },
            { label: '위치 정밀도', value: '±0.05 mm' },
            { label: '반복 정밀도', value: '±0.03 mm' },
            { label: '최대 이동 속도', value: '140 m/min' },
          ],
          tags: ['파이버 레이저', '고속 절단', '정밀 가공'],
        },
      ],
      materials: [
        {
          name: '철판 (SS400)',
          thickness: '최대 30mm',
          note: '일반 구조용 강판',
        },
        {
          name: '스테인리스 (SUS304)',
          thickness: '최대 25mm',
          note: '내식성 우수',
        },
        {
          name: '알루미늄 (AL5052)',
          thickness: '최대 25mm',
          note: '경량 고강도',
        },
      ],
    },
    bending: {
      title: '절곡 가공',
      icon: 'ri-tools-line',
      color: 'gray',
      maxRef: 12,
      machines: [
        {
          name: '프레스 브레이크 (Press Brake)',
          image:
            'https://readdy.ai/api/search-image?query=industrial%20press%20brake%20bending%20machine%20CNC%20metal%20sheet%20forming%20large%20hydraulic%20bending%20equipment%20factory%20workshop%20professional%20photography%20high%20resolution%20detail%20clean%20background&width=700&height=460&seq=press-brake-v2&orientation=landscape',
          specs: [
            { label: '최대 굽힘 길이', value: '3,000 mm' },
            { label: '최대 굽힘 하중', value: '130 Ton' },
            { label: '최소 굽힘 반경', value: '0.5T (판재 두께 기준)' },
            { label: '각도 정밀도', value: '±0.5°' },
            { label: '백게이지 정밀도', value: '±0.1 mm' },
            { label: '최대 가공 두께', value: '12 mm (철판 기준)' },
          ],
          tags: ['CNC 제어', '정밀 절곡', '다양한 형상'],
        },
      ],
      materials: [
        {
          name: '철판',
          thickness: '최대 12mm',
          note: '일반 구조용 강판',
        },
        {
          name: '스테인리스',
          thickness: '최대 9mm',
          note: '정밀 절곡 가능',
        },
        {
          name: '알루미늄',
          thickness: '최대 10mm',
          note: '경량 소재 절곡',
        },
      ],
    },
    welding: {
      title: '용접 가공',
      icon: 'ri-fire-line',
      color: 'red',
      maxRef: 30,
      machines: [
        {
          name: '용접 장비 (TIG / MIG / CO₂)',
          image:
            'https://readdy.ai/api/search-image?query=professional%20TIG%20MIG%20welding%20machine%20industrial%20metal%20fabrication%20workshop%20sparks%20arc%20welding%20stainless%20steel%20aluminum%20equipment%20factory%20environment%20professional%20photography%20high%20resolution&width=700&height=460&seq=welding-equip-v2&orientation=landscape',
          specs: [
            { label: '용접 방식', value: 'TIG / MIG / CO₂' },
            { label: 'TIG 전류 범위', value: '5 ~ 350A' },
            { label: 'MIG 전류 범위', value: '30 ~ 500A' },
            { label: 'CO₂ 전류 범위', value: '60 ~ 500A' },
            { label: '적용 소재', value: '철, 스테인리스, 알루미늄' },
            { label: '최소 판재 두께', value: '0.8 mm' },
          ],
          tags: ['TIG 용접', 'MIG 용접', 'CO₂ 용접'],
        },
      ],
      materials: [
        {
          name: '철판 (SS400)',
          thickness: '0.8mm ~ 30mm',
          note: 'CO₂ / MIG 용접',
        },
        {
          name: '스테인리스 (SUS)',
          thickness: '0.8mm ~ 25mm',
          note: 'TIG 정밀 용접',
        },
        {
          name: '알루미늄',
          thickness: '1.0mm ~ 20mm',
          note: 'TIG / MIG 용접',
        },
        {
          name: '이종 소재',
          thickness: '조건부 가능',
          note: '사전 협의 필요',
        },
      ],
    },
  };

  const current = equipmentSpecs[activeTab];

  // ---- colour maps ---------------------------------------------
  const colorMap: Record<
    string,
    {
      badge: string;
      tag: string;
      border: string;
      tab: string;
      tabActive: string;
    }
  > = {
    orange: {
      badge: 'bg-orange-500',
      tag: 'bg-orange-100 text-orange-700',
      border: 'border-orange-400',
      tab: 'text-gray-600 hover:text-orange-500',
      tabActive: 'bg-orange-500 text-white shadow',
    },
    gray: {
      badge: 'bg-gray-700',
      tag: 'bg-gray-100 text-gray-700',
      border: 'border-gray-500',
      tab: 'text-gray-600 hover:text-gray-800',
      tabActive: 'bg-gray-700 text-white shadow',
    },
    red: {
      badge: 'bg-red-600',
      tag: 'bg-red-100 text-red-700',
      border: 'border-red-400',
      tab: 'text-gray-600 hover:text-red-600',
      tabActive: 'bg-red-600 text-white shadow',
    },
  };

  const tabColors: Record<string, string> = {
    laser: 'bg-orange-500 text-white shadow',
    bending: 'bg-gray-700 text-white shadow',
    welding: 'bg-red-600 text-white shadow',
  };

  const activePopup =
    popupIndex !== null ? processDetails[popupIndex] : null;

  // ---- effect for the small underline animation on process cards
  //      (replaces the previous direct DOM manipulation which caused the syntax error)
  const underlineRef = useRef<(HTMLDivElement | null)[]>([]);
  useEffect(() => {
    const handlers: (() => void)[] = [];
    underlineRef.current.forEach((el) => {
      if (!el) return;
      const mouseEnter = () => {
        el.style.width = '60%';
      };
      const mouseLeave = () => {
        el.style.width = '0%';
      };
      const parent = el.closest('.group');
      parent?.addEventListener('mouseenter', mouseEnter);
      parent?.addEventListener('mouseleave', mouseLeave);
      handlers.push(() => {
        parent?.removeEventListener('mouseenter', mouseEnter);
        parent?.removeEventListener('mouseleave', mouseLeave);
      });
    });
    return () => {
      handlers.forEach((h) => h());
    };
  }, [processDetails]);

  return (
    <>
      <SEOHead
        title="가공능력 | NS-TECH 판금가공"
        description="NS-TECH의 레이저 가공, 절곡 가공, 용접 가공 능력. 6,000W 파이버 레이저, 최대 30mm 철판 절단, ±0.05mm 정밀도로 고품질 가공 서비스를 제공합니다."
        keywords="가공능력, 레이저 가공, 절곡 가공, 용접 가공, 판금 가공 범위, 소재별 두께, 정밀 가공"
        ogType="website"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'Service',
            name: 'NS-TECH 가공능력',
            description: '레이저·절곡·용접 전문 가공 서비스',
            url: `${import.meta.env.VITE_SITE_URL}/equipment/capability`,
            provider: {
              '@type': 'Organization',
              name: 'NS-TECH',
              url: import.meta.env.VITE_SITE_URL,
            },
            areaServed: 'KR',
            serviceType: '판금 가공',
            offers: {
              '@type': 'Offer',
              availability: 'https://schema.org/InStock',
            },
          },
        ]}
      />
      <div className="min-h-screen bg-white">
        {/* 팝업 오버레이 */}
        {activePopup && (
          <div
            className="fixed inset-0 z-[100] flex items-center justify-center p-4"
            style={{
              background: 'rgba(0,0,0,0.55)',
              backdropFilter: 'blur(4px)',
            }}
            onClick={() => setPopupIndex(null)}
          >
            <div
              className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden"
              style={{
                animation:
                  'popupIn 0.28s cubic-bezier(0.34,1.56,0.64,1) both',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* 팝업 헤더 */}
              <div
                className={`flex items-center justify-between px-7 py-5 ${colorPopup[activePopup.color].bg} border-b ${colorPopup[activePopup.color].border}`}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`w-10 h-10 flex items-center justify-center rounded-full ${colorPopup[activePopup.color].badge}`}
                  >
                    <i
                      className={`${activePopup.icon} text-white text-lg`}
                    ></i>
                  </div>
                  <div>
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">
                      Step {activePopup.step}
                    </p>
                    <h3 className="text-gray-900 font-extrabold text-lg leading-tight">
                      {activePopup.title}
                    </h3>
                  </div>
                </div>
                <button
                  onClick={() => setPopupIndex(null)}
                  className="w-8 h-8 flex items-center justify-center rounded-full bg-white/80 hover:bg-white text-gray-500 hover:text-gray-800 transition-all cursor-pointer shadow-sm"
                >
                  <i className="ri-close-line text-lg"></i>
                </button>
              </div>
              {/* 팝업 본문 */}
              <div className="px-7 py-6 space-y-4">
                {activePopup.details.map((d, di) => (
                  <div
                    key={di}
                    className={`flex gap-4 p-4 rounded-2xl border ${colorPopup[activePopup.color].border} ${colorPopup[activePopup.color].bg}`}
                  >
                    <div
                      className={`w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0 ${colorPopup[activePopup.color].badge}`}
                    >
                      <i
                        className={`${d.icon} text-white text-sm`}
                      ></i>
                    </div>
                    <div>
                      <p className="text-gray-900 font-bold text-sm mb-1">
                        {d.title}
                      </p>
                      <p className="text-gray-500 text-xs leading-relaxed">
                        {d.body}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="px-7 pb-6">
                <button
                  onClick={() => setPopupIndex(null)}
                  className={`w-full py-3 rounded-2xl text-white font-bold text-sm transition-all cursor-pointer ${colorPopup[activePopup.color].badge} hover:opacity-90`}
                >
                  확인
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 팝업 애니메이션 키프레임 */}
        <style>{`
          @keyframes popupIn {
            from { opacity: 0; transform: scale(0.88) translateY(24px); }
            to   { opacity: 1; transform: scale(1) translateY(0); }
          }
          @keyframes tabFadeSlideIn {
            from { opacity: 0; transform: translateY(14px); }
            to   { opacity: 1; transform: translateY(0); }
          }
          .tab-content-enter {
            animation: tabFadeSlideIn 0.3s cubic-bezier(0.22,1,0.36,1) both;
          }
        `}</style>

        {/* 네비게이션 */}
        <nav className="fixed top-0 left-0 right-0 z-50 bg-white shadow-md">
          <div className="w-full px-4 lg:px-8 py-3">
            <div className="flex items-center justify-between">
              <div className="hidden lg:flex items-center gap-2">
                <Link
                  to="/"
                  className="flex items-center gap-2 shrink-0 mr-2"
                  onClick={() =>
                    window.scrollTo({ top: 0, behavior: 'instant' })
                  }
                >
                  <img
                    src="https://static.readdy.ai/image/7455c6c7e49181bdad2ed4b44e4d279c/b09fe7069d8db297eae61b8e105adfe6.png"
                    alt="NS-TECH Logo"
                    className="h-12 w-auto object-contain"
                  />
                </Link>
                {navLinks.map((link) =>
                  link.type === 'dropdown' ? (
                    <div
                      key={link.label}
                      className="relative"
                      onMouseEnter={() => {
                        if (link.label === '보유장비')
                          setEquipmentDropdownOpen(true);
                        if (link.label === '제조물품')
                          setProductsDropdownOpen(true);
                        if (link.label === '회사소개')
                          setAboutDropdownOpen(true);
                      }}
                      onMouseLeave={() => {
                        if (link.label === '보유장비')
                          setEquipmentDropdownOpen(false);
                        if (link.label === '제조물품')
                          setProductsDropdownOpen(false);
                        if (link.label === '회사소개')
                          setAboutDropdownOpen(false);
                      }}
                    >
                      <button
                        className={`text-sm font-medium transition-colors whitespace-nowrap px-4 py-2 rounded-md flex items-center gap-1 cursor-pointer ${
                          link.label === '보유장비'
                            ? 'text-orange-500 bg-orange-50'
                            : 'text-gray-700 hover:text-orange-500 hover:bg-orange-50'
                        }`}
                      >
                        {link.label}
                        <i
                          className={`ri-arrow-down-s-line transition-transform ${
                            (link.label === '보유장비' &&
                              equipmentDropdownOpen) ||
                            (link.label === '제조물품' &&
                              productsDropdownOpen) ||
                            (link.label === '회사소개' && aboutDropdownOpen)
                              ? 'rotate-180'
                              : ''
                          }`}
                        ></i>
                      </button>
                      {((link.label === '보유장비' && equipmentDropdownOpen) ||
                        (link.label === '제조물품' && productsDropdownOpen) ||
                        (link.label === '회사소개' && aboutDropdownOpen)) && (
                        <div className="absolute top-full left-0 mt-1 bg-white shadow-lg rounded-lg py-2 min-w-[160px] border border-gray-100">
                          {link.subMenu?.map((subLink) =>
                            subLink.action ? (
                              <button
                                key={subLink.href}
                                onClick={subLink.action}
                                className={`w-full text-left block px-4 py-2 text-sm hover:text-orange-500 hover:bg-orange-50 transition-colors whitespace-nowrap cursor-pointer ${
                                  subLink.href === '/equipment/capability'
                                    ? 'text-orange-500 font-semibold'
                                    : 'text-gray-700'
                                }`}
                              >
                                {subLink.label}
                              </button>
                            ) : (
                              <Link
                                key={subLink.href}
                                to={subLink.href}
                                className={`block px-4 py-2 text-sm hover:text-orange-500 hover:bg-orange-50 transition-colors whitespace-nowrap ${
                                  subLink.href === '/equipment/capability'
                                    ? 'text-orange-500 font-semibold'
                                    : 'text-gray-700'
                                }`}
                              >
                                {subLink.label}
                              </Link>
                            )
                          )}
                        </div>
                      )}
                    </div>
                  ) : link.action ? (
                    <button
                      key={link.href}
                      onClick={link.action}
                      className="text-sm font-medium transition-colors whitespace-nowrap px-4 py-2 rounded-md text-gray-700 hover:text-orange-500 hover:bg-orange-50 cursor-pointer"
                    >
                      {link.label}
                    </button>
                  ) : (
                    <Link
                      key={link.href}
                      to={link.href}
                      className="text-sm font-medium transition-colors whitespace-nowrap px-4 py-2 rounded-md text-gray-700 hover:text-orange-500 hover:bg-orange-50"
                    >
                      {link.label}
                    </Link>
                  )
                )}
              </div>

              {/* 고객지원 버튼 */}
              <div className="hidden lg:flex items-center gap-3 relative">
                <button
                  onClick={() => setSupportOpen(!supportOpen)}
                  className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap shadow-sm"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-customer-service-2-line text-white text-sm"></i>
                  </div>
                  고객지원
                </button>
                {supportOpen && (
                  <div className="absolute top-full right-0 mt-2 bg-white rounded-2xl shadow-2xl border border-gray-100 p-5 w-72 z-50">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-bold text-gray-900 text-sm">
                        고객지원 연락처
                      </h3>
                      <button
                        onClick={() => setSupportOpen(false)}
                        className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
                      >
                        <i className="ri-close-line text-base"></i>
                      </button>
                    </div>
                    <div className="space-y-3">
                      {[
                        {
                          icon: 'ri-phone-line',
                          label: '전화',
                          value: '031-541-1902',
                          color: 'text-orange-500',
                        },
                        {
                          icon: 'ri-printer-line',
                          label: '팩스',
                          value: '031-541-1903',
                          color: 'text-gray-500',
                        },
                        {
                          icon: 'ri-mail-line',
                          label: '이메일',
                          value: 'nstech01@naver.com',
                          color: 'text-orange-500',
                        },
                        {
                          icon: 'ri-time-line',
                          label: '운영시간',
                          value: '평일 09:00 ~ 18:00',
                          color: 'text-gray-500',
                        },
                      ].map((item, i) => (
                        <div
                          key={i}
                          className="flex items-center gap-3 p-2.5 bg-gray-50 rounded-xl"
                        >
                          <div className="w-8 h-8 flex items-center justify-center bg-orange-100 rounded-full flex-shrink-0">
                            <i
                              className={`${item.icon} ${item.color} text-sm`}
                            ></i>
                          </div>
                          <div>
                            <p className="text-xs text-gray-400">{item.label}</p>
                            <p className="text-sm font-semibold text-gray-800">
                              {item.value}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-4 grid grid-cols-2 gap-2">
                      <a
                        href="tel:031-541-1902"
                        className="flex items-center justify-center gap-1.5 bg-orange-500 hover:bg-orange-600 text-white py-2.5 rounded-xl text-xs font-bold transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-phone-fill text-sm"></i> 전화하기
                      </a>
                      <a
                        href="mailto:nstech01@naver.com"
                        className="flex items-center justify-center gap-1.5 bg-gray-900 hover:bg-gray-800 text-white py-2.5 rounded-xl text-xs font-bold transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-mail-fill text-sm"></i> 메일보내기
                      </a>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex lg:hidden items-center justify-between w-full">
                <Link
                  to="/"
                  onClick={() => window.scrollTo({ top: 0, behavior: 'instant' })}
                >
                  <img
                    src="https://static.readdy.ai/image/7455c6c7e49181bdad2ed4b44e4d279c/b09fe7069d8db297eae61b8e105adfe6.png"
                    alt="NS-TECH Logo"
                    className="h-10 w-auto object-contain"
                  />
                </Link>
                <button
                  className="flex flex-col justify-center items-center w-10 h-10 gap-1.5 cursor-pointer relative z-[60]"
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  aria-label="메뉴 열기"
                >
                  <span
                    className={`block w-6 h-0.5 transition-all duration-300 bg-gray-800 ${
                      mobileMenuOpen ? 'rotate-45 translate-y-2' : ''
                    }`}
                  ></span>
                  <span
                    className={`block w-6 h-0.5 transition-all duration-300 bg-gray-800 ${
                      mobileMenuOpen ? 'opacity-0' : ''
                    }`}
                  ></span>
                  <span
                    className={`block w-6 h-0.5 transition-all duration-300 bg-gray-800 ${
                      mobileMenuOpen ? '-rotate-45 -translate-y-2' : ''
                    }`}
                  ></span>
                </button>
              </div>
            </div>
          </div>

          {/* 모바일 메뉴 */}
          <div
            className={`lg:hidden fixed inset-0 bg-black/50 transition-opacity duration-300 z-40 ${
              mobileMenuOpen
                ? 'opacity-100 pointer-events-auto'
                : 'opacity-0 pointer-events-none'
            }`}
            onClick={() => setMobileMenuOpen(false)}
          ></div>
          <div
            className={`lg:hidden fixed top-[60px] left-0 right-0 transition-all duration-300 ease-in-out z-50 ${
              mobileMenuOpen
                ? 'translate-y-0 opacity-100'
                : '-translate-y-4 opacity-0 pointer-events-none'
            }`}
          >
            <div className="bg-white shadow-xl border-t border-gray-100 px-5 py-3 flex flex-col gap-1">
              {navLinks.map((link) =>
                link.type === 'dropdown' ? (
                  <div key={link.label}>
                    <button
                      onClick={() => {
                        if (link.label === '보유장비')
                          setMobileEquipmentOpen(!mobileEquipmentOpen);
                        if (link.label === '제조물품')
                          setMobileProductsOpen(!mobileProductsOpen);
                        if (link.label === '회사소개')
                          setMobileAboutOpen(!mobileAboutOpen);
                      }}
                      className="w-full text-sm font-medium py-2.5 px-4 rounded-lg transition-all cursor-pointer text-left text-gray-700 hover:text-orange-500 hover:bg-orange-50 flex items-center justify-between"
                    >
                      {link.label}
                      <i
                        className={`ri-arrow-down-s-line transition-transform ${
                          (link.label === '보유장비' && mobileEquipmentOpen) ||
                          (link.label === '제조물품' && mobileProductsOpen) ||
                          (link.label === '회사소개' && mobileAboutOpen)
                            ? 'rotate-180'
                            : ''
                        }`}
                      ></i>
                    </button>
                    {((link.label === '보유장비' && mobileEquipmentOpen) ||
                      (link.label === '제조물품' && mobileProductsOpen) ||
                      (link.label === '회사소개' && mobileAboutOpen)) && (
                      <div className="pl-4 mt-1 space-y-1">
                        {link.subMenu?.map((subLink) =>
                          subLink.action ? (
                            <button
                              key={subLink.href}
                              onClick={() => {
                                subLink.action?.();
                                setMobileMenuOpen(false);
                              }}
                              className="w-full text-left block text-sm font-medium py-2 px-4 rounded-lg transition-all cursor-pointer text-gray-600 hover:text-orange-500 hover:bg-orange-50"
                            >
                              {subLink.label}
                            </button>
                          ) : (
                            <Link
                              key={subLink.href}
                              to={subLink.href}
                              onClick={() => setMobileMenuOpen(false)}
                              className="block text-sm font-medium py-2 px-4 rounded-lg transition-all cursor-pointer text-gray-600 hover:text-orange-500 hover:bg-orange-50"
                            >
                              {subLink.label}
                            </Link>
                          )
                        )}
                      </div>
                    )}
                  </div>
                ) : link.action ? (
                  <button
                    key={link.href}
                    onClick={() => {
                      link.action?.();
                      setMobileMenuOpen(false);
                    }}
                    className="w-full text-left text-sm font-medium py-2.5 px-4 rounded-lg transition-all cursor-pointer text-gray-700 hover:text-orange-500 hover:bg-orange-50"
                  >
                    {link.label}
                  </button>
                ) : (
                  <Link
                    key={link.href}
                    to={link.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className="text-sm font-medium py-2.5 px-4 rounded-lg transition-all cursor-pointer text-gray-700 hover:text-orange-500 hover:bg-orange-50"
                  >
                    {link.label}
                  </Link>
                )
              )}
              <div className="border-t border-gray-100 mt-1 pt-2">
                <button
                  onClick={() => setSupportOpen(!supportOpen)}
                  className="w-full text-left text-sm font-medium py-2.5 px-4 rounded-lg transition-all cursor-pointer text-orange-500 hover:bg-orange-50 flex items-center gap-2"
                >
                  <i className="ri-customer-service-2-line"></i> 고객지원
                </button>
                {supportOpen && (
                  <div className="px-4 pb-2 space-y-2">
                    {[
                      {
                        icon: 'ri-phone-line',
                        label: '전화',
                        value: '031-541-1902',
                      },
                      {
                        icon: 'ri-mail-line',
                        label: '이메일',
                        value: 'nstech01@naver.com',
                      },
                    ].map((item, i) => (
                      <div
                        key={i}
                        className="flex items-center gap-2 text-sm text-gray-700"
                      >
                        <i className={`${item.icon} text-orange-500`}></i>
                        <span>{item.value}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </nav>

        {/* 히어로 헤더 */}
        <div className="relative pt-[72px] overflow-hidden">
          <div className="w-full h-56 md:h-72 relative">
            <img
              src="https://readdy.ai/api/search-image?query=modern%20industrial%20laser%20cutting%20machine%20fiber%20laser%20processing%20thick%20metal%20steel%20sheet%20sparks%20flying%20precision%20CNC%20manufacturing%20factory%20dark%20dramatic%20lighting%20professional%20photography%20wide%20angle&width=1400&height=500&seq=capability-hero-v3&orientation=landscape"
              alt="가공능력 배경"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-gray-900/80 via-gray-900/60 to-transparent"></div>
            <div className="absolute inset-0 flex flex-col justify-center px-8 md:px-20">
              <div className="max-w-6xl mx-auto w-full">
                <span className="text-orange-400 text-xs font-semibold uppercase tracking-widest">
                  Processing Capability
                </span>
                <h1 className="text-3xl md:text-5xl font-bold text-white mt-2 mb-3">
                  가공능력
                </h1>
                <p className="text-gray-300 text-sm md:text-base">
                  장비 사양 · 가공 범위 · 소재별 최대 두께
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* ── 요약 스탯 (카운트업 + 호버 효과) ── */}
        <section className="bg-gray-900 py-8 px-5 md:px-20">
          <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4">
            {[
              {
                icon: 'ri-focus-3-line',
                value: '6,000W',
                label: '레이저 출력',
                sub: 'Smart Fiber',
                delay: 0,
              },
              {
                icon: 'ri-expand-left-right-line',
                value: '3,048mm',
                label: '최대 가공 길이',
                sub: '레이저 기준',
                delay: 80,
              },
              {
                icon: 'ri-stack-line',
                value: '최대 30mm',
                label: '최대 가공 두께',
                sub: '철판 기준',
                delay: 160,
              },
              {
                icon: 'ri-crosshair-line',
                value: '±0.05mm',
                label: '위치 정밀도',
                sub: '레이저 절단',
                delay: 240,
              },
            ].map((stat, i) => (
              <StatCard key={i} {...stat} />
            ))}
          </div>
        </section>

        {/* ── 탭 선택 ── */}
        <section className="py-10 px-5 md:px-20 bg-white">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center gap-3 mb-8 flex-wrap">
              {(
                [
                  {
                    key: 'laser',
                    label: '레이저 가공',
                    icon: 'ri-focus-3-line',
                  },
                  {
                    key: 'bending',
                    label: '절곡 가공',
                    icon: 'ri-tools-line',
                  },
                  {
                    key: 'welding',
                    label: '용접 가공',
                    icon: 'ri-fire-line',
                  },
                ] as const
              ).map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => handleTabChange(tab.key)}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap ${
                    activeTab === tab.key
                      ? tabColors[tab.key]
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={`${tab.icon} text-sm`}></i>
                  </div>
                  {tab.label}
                </button>
              ))}
            </div>

            {/* ── 탭 콘텐츠 (페이드+슬라이드 전환) ── */}
            <div
              key={activeTab}
              className={tabVisible ? 'tab-content-enter' : ''}
              style={{
                opacity: tabVisible ? 1 : 0,
                transition: 'opacity 0.18s ease',
              }}
            >
              {/* 장비 상세 */}
              {current.machines.map((machine, mi) => (
                <div key={mi} className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-10 mb-10">
                  <div className="relative rounded-2xl overflow-hidden shadow-md">
                    <div className="w-full h-64 md:h-80">
                      <img src={machine.image} alt={machine.name} className="w-full h-full object-cover object-top" />
                    </div>
                    <div className="absolute top-4 left-4 flex flex-wrap gap-2">
                      {machine.tags.map((tag, ti) => (
                        <span key={ti} className={`px-3 py-1 rounded-full text-xs font-bold text-white whitespace-nowrap ${colorMap[current.color].badge}`}>
                          {tag}
                        </span>
                      ))}
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-5 py-4">
                      <p className="text-white font-bold text-sm md:text-base">{machine.name}</p>
                    </div>
                  </div>

                  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                    <div className="bg-gray-900 px-6 py-4 flex items-center gap-3">
                      <div className={`w-8 h-8 flex items-center justify-center ${colorMap[current.color].badge} rounded-full`}>
                        <i className={`${current.icon} text-white text-sm`}></i>
                      </div>
                      <h3 className="text-white font-bold text-sm md:text-base">장비 사양</h3>
                    </div>
                    <div className="divide-y divide-gray-50">
                      {machine.specs.map((spec, si) => (
                        <div key={si} className="flex items-center justify-between px-5 py-3 hover:bg-orange-50/30 transition-colors">
                          <span className="text-xs md:text-sm text-gray-500 font-medium">{spec.label}</span>
                          <span className="text-xs md:text-sm text-gray-900 font-bold">{spec.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}

              {/* 소재별 가공 범위 */}
              <div className="mt-4">
                <div className="flex items-center gap-2 mb-5">
                  <div className={`w-8 h-8 flex items-center justify-center ${colorMap[current.color].badge} rounded-full`}>
                    <i className="ri-stack-line text-white text-sm"></i>
                  </div>
                  <h3 className="text-gray-900 font-bold text-base md:text-lg">소재별 가공 범위</h3>
                </div>
                <div className="flex flex-wrap justify-center gap-4 md:gap-6">
                  {current.materials.map((mat, mi) => {
                    const iconList = ['ri-layers-line', 'ri-shield-check-line', 'ri-flashlight-line', 'ri-link-m'];
                    const icon = iconList[mi % iconList.length];
                    return (
                      <div
                        key={mi}
                        className="group relative bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-2xl transition-all duration-300 overflow-hidden cursor-default hover:-translate-y-2 w-full sm:w-[calc(50%-12px)] md:w-[calc(25%-18px)] min-w-[200px] max-w-[280px]"
                        style={{ borderTop: 'none' }}
                      >
                        <div className={`h-1.5 w-full ${colorMap[current.color].badge} group-hover:h-2 transition-all duration-300`}></div>
                        <div
                          className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none ${
                            current.color === 'orange'
                              ? 'bg-gradient-to-br from-orange-50/80 via-white to-orange-50/40'
                              : current.color === 'gray'
                              ? 'bg-gradient-to-br from-gray-50/80 via-white to-gray-100/40'
                              : 'bg-gradient-to-br from-red-50/80 via-white to-red-50/40'
                          }`}
                        ></div>
                        <div
                          className={`absolute -bottom-6 -right-6 w-20 h-20 rounded-full opacity-5 group-hover:opacity-100 transition-all duration-300 scale-100 group-hover:scale-125 ${colorMap[current.color].badge}`}
                        ></div>
                        <div className="relative z-10 p-5 flex flex-col gap-2">
                          <div className="flex items-center gap-2">
                            <div className={`w-8 h-8 flex items-center justify-center rounded-lg transition-all duration-300 ${colorMap[current.color].tag} group-hover:scale-110`}>
                              <i className={`${icon} text-sm`}></i>
                            </div>
                            <p className="text-gray-800 font-bold text-sm leading-tight">{mat.name}</p>
                          </div>
                          <div>
                            <p className="text-[10px] text-gray-400 font-medium mb-0.5 uppercase tracking-wide">
                              최대 두께
                            </p>
                            <p
                              className={`text-xl font-extrabold leading-tight transition-all duration-300 ${
                                current.color === 'orange'
                                  ? 'text-orange-500 group-hover:text-orange-600'
                                  : current.color === 'gray'
                                  ? 'text-gray-700 group-hover:text-gray-900'
                                  : 'text-red-500 group-hover:text-red-600'
                              }`}
                            >
                              {mat.thickness}
                            </p>
                          </div>
                          {/* 프로그레스 바 */}
                          <ThicknessBar
                            thickness={mat.thickness}
                            maxRef={(current as any).maxRef ?? 30}
                            color={current.color}
                          />
                          <div
                            className={`h-px w-full transition-all duration-300 ${
                              current.color === 'orange'
                                ? 'bg-orange-100 group-hover:bg-orange-200'
                                : current.color === 'gray'
                                ? 'bg-gray-100 group-hover:bg-gray-200'
                                : 'bg-red-100 group-hover:bg-red-200'
                            }`}
                          ></div>
                          <div className="flex items-center justify-between">
                            <span
                              className={`px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-all duration-300 ${colorMap[current.color].tag}`}
                            >
                              {mat.note}
                            </span>
                            <div
                              className={`w-6 h-6 flex items-center justify-center rounded-full opacity-0 group-hover:opacity-100 transition-all duration-300 translate-x-2 group-hover:translate-x-0 ${colorMap[current.color].badge}`}
                            >
                              <i className="ri-arrow-right-s-line text-white text-sm"></i>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── 가공 공정 흐름 (단계별 팝업) ── */}
        <section className="py-14 px-5 md:px-20 bg-gray-50">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-10">
              <span className="text-orange-500 text-xs font-semibold uppercase tracking-widest">
                Process Flow
              </span>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-2 mb-3">
                가공 공정 흐름
              </h2>
              <div className="w-10 h-1 bg-orange-500 mx-auto rounded-full mb-2"></div>
              <p className="text-gray-400 text-xs">
                각 단계를 클릭하면 상세 정보를 확인할 수 있습니다
              </p>
            </div>
            <div className="flex flex-col md:flex-row items-stretch justify-center gap-0">
              {processDetails.map((item, i, arr) => (
                <div key={i} className="flex flex-row md:flex-col items-center gap-0 w-full md:flex-1">
                  <button
                    onClick={() => setPopupIndex(i)}
                    className={`group flex flex-col items-center justify-center rounded-2xl shadow-sm border px-4 py-5 w-full text-center gap-2 hover:shadow-xl transition-all duration-300 min-h-[160px] cursor-pointer hover:-translate-y-1 relative overflow-hidden ${item.cardBg} ${item.cardBorder}`}
                  >
                    {/* 호버 배경 글로우 */}
                    <div
                      className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                      style={{
                        background: `radial-gradient(circle at 50% 40%, ${item.glowColor} 0%, transparent 70%)`,
                      }}
                    />
                    <span className={`text-xs font-bold relative z-10 ${item.stepColor}`}>{item.step}</span>
                    <div className={`w-10 h-10 flex items-center justify-center ${item.iconBg} group-hover:opacity-90 rounded-full shrink-0 transition-all duration-300 group-hover:scale-110 group-hover:shadow-lg relative z-10`}>
                      <i className={`${item.icon} text-white text-lg`}></i>
                    </div>
                    <p className="text-gray-900 font-bold text-xs md:text-sm relative z-10">{item.title}</p>
                    <p className={`text-xs leading-relaxed relative z-10 ${item.labelColor} font-medium`}>{item.desc}</p>
                    {/* 클릭 힌트 */}
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all duration-300 translate-y-1 group-hover:translate-y-0 relative z-10">
                      <i className={`ri-information-line text-xs ${item.stepColor}`}></i>
                      <span className={`text-[10px] font-bold whitespace-nowrap ${item.stepColor}`}>상세보기</span>
                    </div>
                    {/* 하단 강조 바 */}
                    <div
                      ref={(el) => {
                        if (el) underlineRef.current[i] = el;
                      }}
                      className="absolute bottom-0 left-1/2 -translate-x-1/2 h-0.5 rounded-full transition-all duration-300"
                      style={{ width: '0%' }}
                    />
                  </button>
                  {i < arr.length - 1 && (
                    <div className="flex items-center justify-center w-8 h-8 flex-shrink-0 md-self-center">
                      <i className="ri-arrow-right-s-line text-gray-400 text-2xl hidden md:block"></i>
                      <i className="ri-arrow-down-s-line text-gray-400 text-2xl md:hidden block"></i>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-12 px-5 md:px-20 bg-gray-900">
          <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-white text-xl md:text-2xl font-bold mb-1">
                가공 문의가 있으신가요?
              </h3>
              <p className="text-gray-400 text-sm">
                도면이나 사양을 보내주시면 빠르게 견적을 드립니다.
              </p>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <a
                href="tel:031-541-1902"
                className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-full text-sm font-bold transition-all cursor-pointer whitespace-nowrap shadow-lg"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-phone-fill text-white text-sm"></i>
                </div>
                031-541-1902
              </a>
              <a
                href="mailto:nstech01@naver.com"
                className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-6 py-3 rounded-full text-sm font-bold transition-all cursor-pointer whitespace-nowrap border border-white/20"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-mail-line text-white text-sm"></i>
                </div>
                nstech01@naver.com
              </a>
            </div>
          </div>
        </section>

        {/* 푸터 */}
        <footer className="bg-gray-800 text-white py-10 px-5 md:px-20">
          <div className="max-w-6xl mx-auto text-center">
            <p className="text-sm text-gray-400">
              © 2024 NS-TECH. All rights reserved.
            </p>
          </div>
        </footer>

        {/* 모바일 플로팅 전화 버튼 */}
        <a
          href="tel:031-541-1902"
          className="lg:hidden fixed bottom-24 right-4 z-50 flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white pl-3 pr-4 py-2.5 rounded-full shadow-xl transition-all active:scale-95 cursor-pointer"
          aria-label="전화 연결"
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-phone-fill text-white text-base"></i>
          </div>
          <span className="text-xs font-bold whitespace-nowrap">전화문의</span>
        </a>
      </div>
    </>
  );
}
